<template>
    <div class="datepicker-input-group">
        <VueDatePicker 
            :modelValue="local_date"
            @update:modelValue="updateDate"
            :time-picker="false"
            :enable-time-picker="true"
            :time-picker-inline="true"
            :format="inputFormat"
            week-start="0"
            :placeholder="placeholder"
            :input-class="inputClass"
            :class="customClass"
            :position="position"
            auto-apply
            :disabled="disabled"
            teleport>
        </VueDatePicker>
        <span class="timezone-display ms-2 text-muted small">
            {{ timezoneDisplay }}
        </span>
    </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'DatetimePickerLocal',
    components: {
        VueDatePicker
    },
    props: {
        placeholder: {
            type: String,
            default: 'Select date and time'
        },
        modelValue: {
            type: [Date, String],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        inputFormat: {
            type: String,
            default: 'MM/dd/yyyy hh:mm a'
        },
        inputClass: {
            type: String,
            default: 'form-control'
        },
        customClass: {
            type: String,
            default: ''
        },
        position: {
            type: String,
            default: 'left'
        }
    },
    data() {
        return {
            local_date: null
        }
    },
    computed: {
        timezoneDisplay() {
            try {
                const now = new Date();
                
                // Get timezone abbreviation (e.g., CST, EST, PST)
                const timeZoneAbbr = now.toLocaleTimeString('en-US', { 
                    timeZoneName: 'short' 
                }).split(' ').pop();
                return `${timeZoneAbbr}`;
            } catch (e) {
                const offset = new Date().getTimezoneOffset();
                const offsetHours = Math.floor(Math.abs(offset) / 60);
                const offsetMinutes = Math.abs(offset) % 60;
                const sign = offset <= 0 ? '-' : '+';
                const gmtOffset = offsetMinutes > 0 
                    ? `GMT${sign}${offsetHours}:${String(offsetMinutes).padStart(2, '0')}`
                    : `GMT${sign}${offsetHours}`;
                return `Local (${gmtOffset})`;
            }
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    // Handle both Date and String inputs
                    // JavaScript automatically converts UTC strings to local time when creating Date objects
                    const utcDate = newValue instanceof Date ? new Date(newValue) : new Date(newValue);
                    
                    // Extract LOCAL time components (not UTC) to create a Date object
                    // that VueDatePicker will interpret as local time
                    // This ensures 4:00 PM UTC displays as 10:00 AM CST (for example)
                    const localDate = new Date(
                        utcDate.getFullYear(),      // Local year
                        utcDate.getMonth(),         // Local month
                        utcDate.getDate(),          // Local date
                        utcDate.getHours(),         // Local hours (already converted from UTC)
                        utcDate.getMinutes(),       // Local minutes
                        utcDate.getSeconds(),      // Local seconds
                        utcDate.getMilliseconds()  // Local milliseconds
                    );
                    
                    this.local_date = localDate;
                } else {
                    this.local_date = null;
                }
            }
        }
    },
    methods: {
        updateDate(newDate) {
            if (newDate) {
                // Handle regular date selection
                // newDate is a Date object from the date picker representing local time
                // The Date object internally stores time as UTC (milliseconds since epoch)
                // So we can use it directly - JavaScript handles the conversion
                const localDate = new Date(newDate);
                
                // The Date object already represents the correct moment in time
                // When we emit it, it will be serialized correctly as UTC
                // No need to manually convert - the Date object handles timezone internally
                this.$emit('update:modelValue', localDate);
            } else {
                this.$emit('update:modelValue', null);
            }
        },
        clearDate() {
            this.local_date = null;
            this.$emit('update:modelValue', null);
        }
    }
})
</script>